/**
 * Metaobject Search Module
 *
 * Adds metaobject search capability to the predictive search drawer.
 * Configure the METAOBJECT_SEARCH_CONFIG array below to add/modify searchable metaobject types.
 */

// =============================================================================
// CONFIGURATION - Modify this array to add/change searchable metaobject types
// =============================================================================
const METAOBJECT_SEARCH_CONFIG = [
  {
    type: 'reference',                        // Metaobject type handle
    label: window.solinfoConfig?.translations?.referencesTab || 'References',  // Tab label (translated)
    searchFields: ['title', 'description'],   // Fields to search in (case-insensitive)
    displayFields: {
      title: 'title',                         // Field to use for display title
      image: 'image',                         // Field to use for thumbnail (optional)
    },
    limit: 25                                 // Maximum results to show
  },
  // Add more metaobject types here:
  // {
  //   type: 'case-study',
  //   label: 'Case Studies',
  //   searchFields: ['title', 'summary'],
  //   displayFields: { title: 'title', image: 'featured_image' },
  //   limit: 5
  // }
];

// =============================================================================
// IMPLEMENTATION - Generally no need to modify below this line
// =============================================================================

class MetaobjectSearch {
  constructor() {
    this._cache = new Map();
    this._pendingFetches = new Map();
  }

  /**
   * Get the Storefront API token
   */
  getApiToken() {
    return window.shopifyStorefrontToken ||
      window.solinfoConfig?.apiToken ||
      null;
  }

  /**
   * Get the current locale
   */
  getLocale() {
    return window.solinfoConfig?.locale ||
      document.documentElement.lang?.toUpperCase() ||
      'EN';
  }

  /**
   * Build GraphQL query for fetching metaobjects
   */
  buildQuery(metaobjectType, cursor = null) {
    const locale = this.getLocale();
    const afterParam = cursor ? `, after: "${cursor}"` : '';
    return `
      query GetMetaobjects @inContext(language: ${locale}) {
        metaobjects(type: "${metaobjectType}", first: 250${afterParam}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id
            handle
            fields {
              key
              value
              reference {
                ... on MediaImage {
                  image {
                    url(transform: { maxWidth: 192, maxHeight: 192, crop: CENTER })
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  /**
   * Fetch metaobjects from Storefront API
   */
  async fetchMetaobjects(metaobjectType, abortSignal) {
    const cacheKey = `${metaobjectType}_${this.getLocale()}`;

    // Return cached data if available
    if (this._cache.has(cacheKey)) {
      return this._cache.get(cacheKey);
    }

    // If already fetching, wait for that request
    if (this._pendingFetches.has(cacheKey)) {
      return this._pendingFetches.get(cacheKey);
    }

    const apiToken = this.getApiToken();
    if (!apiToken) {
      console.warn('MetaobjectSearch: No Storefront API token found');
      return [];
    }

    const fetchPromise = (async () => {
      try {
        let allNodes = [];
        let hasNextPage = true;
        let cursor = null;

        while (hasNextPage) {
          const response = await fetch('/api/2025-04/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': apiToken,
            },
            body: JSON.stringify({ query: this.buildQuery(metaobjectType, cursor) }),
            signal: abortSignal,
          });

          const data = await response.json();

          if (data.errors) {
            console.error('MetaobjectSearch: GraphQL errors', data.errors);
            if (allNodes.length === 0) return [];
            break;
          }

          const nodes = data.data?.metaobjects?.nodes || [];
          allNodes = allNodes.concat(nodes);

          const pageInfo = data.data?.metaobjects?.pageInfo;
          hasNextPage = pageInfo?.hasNextPage || false;
          cursor = pageInfo?.endCursor || null;
        }

        // Transform nodes to a more usable format
        const transformed = allNodes.map(node => {
          const fields = {};
          node.fields.forEach(field => {
            if (field.reference?.image?.url) {
              fields[field.key] = { value: field.value, imageUrl: field.reference.image.url };
            } else {
              fields[field.key] = { value: field.value };
            }
          });
          return {
            id: node.id,
            handle: node.handle,
            fields,
            url: `/pages/${metaobjectType}/${node.handle}`
          };
        });

        this._cache.set(cacheKey, transformed);
        return transformed;
      } catch (error) {
        if (error.name === 'AbortError') {
          throw error;
        }
        console.error('MetaobjectSearch: Fetch error', error);
        return [];
      } finally {
        this._pendingFetches.delete(cacheKey);
      }
    })();

    this._pendingFetches.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  /**
   * Extract plain text from Storefront API Rich Text JSON format
   */
  extractTextFromRichText(node) {
    if (!node) return '';
    let text = '';
    
    const isBlockElement = node.type && ['paragraph', 'heading', 'list-item'].includes(node.type);

    if (typeof node.value === 'string') {
      text += node.value;
    }
    if (Array.isArray(node.children)) {
      node.children.forEach(child => {
        text += this.extractTextFromRichText(child);
      });
    }

    if (isBlockElement) {
      text += ' ';
    }

    return text;
  }

  /**
   * Strip HTML tags from a string or parse Rich Text JSON
   */
  stripHtml(html) {
    if (!html) return '';

    // Shopify rich text fields return stringified JSON
    const trimmed = html.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map(item => typeof item === 'string' ? item : this.extractTextFromRichText(item)).join(' ').replace(/\s+/g, ' ').trim();
        }
        return this.extractTextFromRichText(parsed).replace(/\s+/g, ' ').trim();
      } catch (e) {
        // Fallback to HTML stripping if JSON parsing fails
      }
    }

    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  }

  /**
   * Remove diacritics/accents for robust searching
   */
  normalizeText(text) {
    if (!text) return '';
    return text.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  /**
   * Filter metaobjects by search query
   */
  filterByQuery(metaobjects, query, searchFields) {
    const normalizedQuery = this.normalizeText(query);
    if (!normalizedQuery) return [];

    return metaobjects.filter(obj => {
      return searchFields.some(fieldName => {
        const fieldData = obj.fields[fieldName];
        if (!fieldData?.value) return false;

        const fieldValue = this.normalizeText(this.stripHtml(fieldData.value));
        return fieldValue.includes(normalizedQuery);
      });
    });
  }

  /**
   * Search all configured metaobject types
   */
  async search(query, abortSignal) {
    const results = {};

    await Promise.all(
      METAOBJECT_SEARCH_CONFIG.map(async (config) => {
        try {
          const metaobjects = await this.fetchMetaobjects(config.type, abortSignal);
          const filtered = this.filterByQuery(metaobjects, query, config.searchFields);
          const limited = filtered.slice(0, config.limit);

          results[config.type] = {
            config,
            items: limited.map(item => ({
              title: this.stripHtml(item.fields[config.displayFields.title]?.value) || item.handle,
              imageUrl: item.fields[config.displayFields.image]?.imageUrl || null,
              url: item.url,
              handle: item.handle
            }))
          };
        } catch (error) {
          if (error.name === 'AbortError') {
            throw error;
          }
          console.error(`MetaobjectSearch: Error searching ${config.type}`, error);
          results[config.type] = { config, items: [] };
        }
      })
    );

    return results;
  }

  /**
   * Render results as HTML for injection into the search drawer
   */
  renderResults(results) {
    const fragments = [];

    for (const [type, data] of Object.entries(results)) {
      if (data.items.length === 0) continue;

      // Tab title
      const tabTitle = document.createElement('div');
      tabTitle.className = 'predictive-search__tab-item';
      tabTitle.setAttribute('slot', 'title');
      tabTitle.innerHTML = `<button type="button" class="h5">${data.config.label}</button>`;

      // Tab content
      const tabContent = document.createElement('div');
      tabContent.className = 'predictive-search__tab-content v-stack gap-4 sm:gap-6';
      tabContent.setAttribute('slot', 'content');

      data.items.forEach(item => {
        const resultEl = document.createElement('a');
        resultEl.href = item.url;
        resultEl.setAttribute('data-instant', '');
        resultEl.className = 'predictive-search-result group';

        let innerHTML = '';
        if (item.imageUrl) {
          innerHTML += `<img src="${item.imageUrl}" alt="${item.title}" loading="lazy" width="96" height="96">`;
        }
        innerHTML += `
          <div class="v-stack justify-items-start gap-0.5">
            <span class="bold"><span class="reversed-link">${item.title}</span></span>
          </div>
        `;
        resultEl.innerHTML = innerHTML;
        tabContent.appendChild(resultEl);
      });

      fragments.push({ title: tabTitle, content: tabContent });
    }

    return fragments;
  }

  /**
   * Clear the cache (useful for development or when data changes)
   */
  clearCache() {
    this._cache.clear();
  }
}

// Create global instance
window.metaobjectSearch = new MetaobjectSearch();
